/**
 * Performance d'une branche et échelle de couleur associée.
 *
 * La performance est ramenée à [0, 1] où 0 vaut « meilleur », quelle que soit la
 * grandeur choisie : le rang se lit déjà dans ce sens, le score duplicate est inversé.
 */

import type { DecisionFlowNode, DecisionFlowReport } from './decisionFlowLayout';

export type FlowMetric = 'rank' | 'score';

export const FLOW_METRIC_LABELS: Record<FlowMetric, string> = {
  rank: 'Rang sur la donne',
  score: 'Points matchs',
};

export function branchPerformance(
  node: DecisionFlowNode,
  metric: FlowMetric,
  range: { min: number; max: number },
): number {
  if (metric === 'rank') return node.meanRankPct;
  const span = range.max - range.min;
  if (span <= 0) return 0.5;
  return 1 - (node.meanDealScore - range.min) / span;
}

/** Erreur type de la moyenne, exprimée dans la même unité que `branchPerformance`. */
function performanceStdError(
  node: DecisionFlowNode,
  metric: FlowMetric,
  range: { min: number; max: number },
): number {
  if (metric === 'rank') return node.seRankPct;
  const span = range.max - range.min;
  return span > 0 ? node.seDealScore / span : 0;
}

/** Performance d'une branche dans son unité d'origine, pour l'affichage. */
export function formatBranchPerformance(
  node: DecisionFlowNode,
  metric: FlowMetric,
  totalPlayersOnDeal: number,
): string {
  if (metric === 'score') return `${node.meanDealScore.toFixed(1)} pts`;
  const rank = 1 + node.meanRankPct * Math.max(1, totalPlayersOnDeal - 1);
  return `${rank.toFixed(0)}ᵉ sur ${totalPlayersOnDeal}`;
}

/**
 * Performance de chaque branche, ramenée vers celle dont elle est issue à proportion
 * de ce que l'écart doit au hasard.
 *
 * Sans cela la couleur saute : deux sous-groupes dont les moyennes ne diffèrent que
 * par le bruit d'échantillonnage prennent des teintes franchement distinctes, et l'œil
 * y lit une décision payante là où il n'y a rien. Le poids `d²/(d² + se²)` vaut presque
 * 1 quand l'écart domine son erreur type — la sous-vague prend alors sa vraie couleur —
 * et tend vers 0 quand il s'y noie : la sous-vague garde alors celle de sa mère.
 * La couleur ne change donc qu'où quelque chose a réellement changé.
 */
export function shrunkPerformance(
  report: DecisionFlowReport,
  metric: FlowMetric,
): Map<DecisionFlowNode, number> {
  const out = new Map<DecisionFlowNode, number>();
  const range = report.dealScoreRange;

  const walk = (node: DecisionFlowNode, parentValue: number | null) => {
    const raw = branchPerformance(node, metric, range);
    let value = raw;
    if (parentValue != null) {
      const delta = raw - parentValue;
      const se = performanceStdError(node, metric, range);
      const weight = se > 0 ? (delta * delta) / (delta * delta + se * se) : 1;
      value = parentValue + delta * weight;
    }
    out.set(node, value);
    for (const child of node.children) walk(child, value);
  };

  // La racine n'a pas d'effectif propre : les branches de la première colonne se
  // comparent à la moyenne générale, qui vaut le centre de l'échelle.
  for (const child of report.root.children) walk(child, 0.5);
  out.set(report.root, 0.5);
  return out;
}

export interface FlowColorScale {
  /** Demi-amplitude de l'échelle, en performance normalisée, autour de la moyenne générale. */
  halfSpan: number;
  /** Bornes de l'échelle rendues dans l'unité de la grandeur choisie. */
  bestLabel: string;
  worstLabel: string;
  color(node: DecisionFlowNode): string;
}

const NEUTRAL = 'hsl(52, 30%, 62%)';

/**
 * Échelle centrée sur la moyenne générale de la donne, d'amplitude robuste.
 *
 * Deux ancrages ont été écartés. Tendre l'échelle entre le meilleur et le dernier
 * joueur donnerait des vagues toutes de la même teinte fade : on ne colore pas des
 * joueurs mais des moyennes de sous-groupes, qui se serrent au centre. La tendre entre
 * la meilleure et la moins bonne branche laisse une branche extrême fixer une borne et
 * repousser toutes les autres à l'opposé de l'échelle. On prend donc le 90ᵉ centile des
 * écarts à la moyenne : la couleur dit « mieux ou moins bien que l'ensemble des
 * joueurs », ce qui donne au vert et au rouge un sens stable d'une donne à l'autre.
 */
export function buildColorScale(
  report: DecisionFlowReport,
  metric: FlowMetric,
): FlowColorScale {
  const values = shrunkPerformance(report, metric);

  const deviations = [...values.entries()]
    .filter(([node]) => node !== report.root)
    .map(([, value]) => Math.abs(value - 0.5))
    .sort((a, b) => a - b);
  const halfSpan = deviations.length > 0
    ? Math.max(0.02, deviations[Math.floor(deviations.length * 0.9)] ?? 0.5)
    : 0.5;

  /** Rend un niveau normalisé dans l'unité d'origine. */
  const label = (value: number) => {
    if (metric === 'score') {
      const { min, max } = report.dealScoreRange;
      return `${(min + (1 - value) * (max - min)).toFixed(0)} pts`;
    }
    const rank = 1 + value * Math.max(1, report.totalPlayersOnDeal - 1);
    return `${rank.toFixed(0)}ᵉ sur ${report.totalPlayersOnDeal}`;
  };

  return {
    halfSpan,
    bestLabel: label(Math.max(0, 0.5 - halfSpan)),
    worstLabel: label(Math.min(1, 0.5 + halfSpan)),
    color(node) {
      const value = values.get(node);
      if (value == null) return NEUTRAL;
      const t = Math.max(-1, Math.min(1, (value - 0.5) / halfSpan));
      // Dégradé continu vert (mieux que la moyenne) → ambre → rouge : c'est le seul
      // canal de performance, il ne doit rien encoder d'autre.
      return `hsl(${52 - t * 52}, ${30 + Math.abs(t) * 34}%, ${62 - Math.abs(t) * 6}%)`;
    },
  };
}
